/** The Intake page's words (intake v2 phase 5). */
import type { IntakeAlert, PushRefusal, QueueRow, ShadowComparison } from '../api/types'
import { duration } from './health'

export const ROLE_LABEL: Record<string, string> = {
  fast: 'ingest-fast', slow: 'ingest-slow', health: 'health checks',
  alerts: 'alert rules', mail: 'alert email',
}
const WORKER_SILENT_MS = 180_000

export function workerState(beatAt: string, now = new Date()): 'running' | 'silent' {
  return now.getTime() - new Date(beatAt).getTime() >= WORKER_SILENT_MS ? 'silent' : 'running'
}

export function queueLate(row: QueueRow, now = new Date()): boolean {
  if (row.limit_s === null) return false
  return now.getTime() - new Date(row.oldest_due_at).getTime() > row.limit_s * 1000
}

export function formatBytes(n: number): string {
  return n >= 1e9 ? `${(n / 1e9).toFixed(1)} GB` : `${(n / 1e6).toFixed(1)} MB`
}

/** A platform alert as one sentence. A station's alert uses lib/health's
 *  alertText with the station's name. */
export function platformAlertText(alert: IntakeAlert, now = new Date()): string {
  switch (alert.kind) {
    case 'worker_silent': {
      const role = ROLE_LABEL[alert.detail.role] ?? alert.detail.role
      const last = alert.detail.last_beat_at
      return last ? `${role} has not reported for ${duration(now.getTime() - new Date(last).getTime())}.`
        : `${role} has never reported.`
    }
    case 'queue_lag': {
      const d = alert.detail
      return `${d.due} ${d.task} task${d.due === 1 ? '' : 's'} waiting; the oldest has `
        + `waited ${duration(d.lag_s * 1000)} (limit ${d.limit_s} s).`
    }
    case 'disk':
      return `The database disk is ${Math.round(alert.detail.used_fraction * 100)}% full `
        + `(${formatBytes(alert.detail.free_bytes)} free).`
    default:
      return ''
  }
}

export function refusalText(r: PushRefusal): string {
  if (r.reason === 'sid_mismatch') {
    return `Logger ${r.logger_id}'s login pushed for station id ${r.claimed}.`
  }
  return `Station id ${r.claimed} has no active station.`
}

export function shadowVerdict(c: ShadowComparison): string {
  if (c.passes) {
    return `Ready to move to push: ${c.pushes} pushes over ${c.days} days, `
      + `${c.matched} readings match, nothing differs.`
  }
  if (!c.pushes) return `Not yet: no pushes in the last ${c.days} days.`
  const reasons: string[] = []
  if (c.differing) reasons.push(`${c.differing} reading${c.differing === 1 ? '' : 's'} differ`)
  const missing = c.missing.reduce((n, m) => n + m.readings, 0)
  if (missing) reasons.push(`${missing} readings stored from MDS are in no push`)
  if (c.unmapped.length) reasons.push(`${c.unmapped.length} pushed channels have no mapping`)
  if (c.failed) reasons.push(`${c.failed} pushes could not be read`)
  return `Not yet: ${reasons.join('; ')}.`
}
